import { useEffect, useState, useContext } from 'react';
import { hallPassApiService, MapPathUserAssignment, MapPath, MapLocation } from '../services/hallPassApiService';
import { useNotification } from '../contexts/NotificationContext';
import { UserContext } from '../contexts/UserContext';
import { Edit, Trash, Search, X, ChevronUp, ChevronDown } from 'lucide-react';

// Local UI model for a user's ordered path assignment
export interface UserPathPreference {
  id: string; // temp id or persisted id as string
  userId: string;
  pathId: string;
  sortOrder: number;
  isPrimary?: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  mapPathUserId?: number; // numeric PK when saved
}

interface EditingState {
  userId: string;
  userName: string;
  preferences: UserPathPreference[];
}

const UserHallPassTypesPage = () => {
  const { userProfile } = useContext(UserContext);
  const { addNotification } = useNotification();

  const [preferences, setPreferences] = useState<UserPathPreference[]>([]);
  const [paths, setPaths] = useState<MapPath[]>([]);
  const [locations, setLocations] = useState<MapLocation[]>([]);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingChanges, setEditingChanges] = useState<{
    toCreate: { hallPassTypeId: string; rankOrder: number }[];
    toUpdate: { id: string; hallPassTypeId: string; rankOrder: number }[];
    toDelete: string[];
  }>({ toCreate: [], toUpdate: [], toDelete: [] });
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const users = (userProfile?.teachers || []).map(t => ({ 
    id: t.id, 
    name: t.name, 
    type: t.mainRole || 'Teacher' 
  }));

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [assignments, mapPaths, locs] = await Promise.all([
        hallPassApiService.getMapPathUsers(),
        hallPassApiService.getMapPaths(),
        hallPassApiService.getMapLocations().catch(() => []),
      ]) as [MapPathUserAssignment[], MapPath[], MapLocation[]];
      setPreferences(assignments.map(a => ({
        id: String(a.mapPathUserId),
        mapPathUserId: a.mapPathUserId,
        userId: a.userId,
        pathId: a.pathId,
        sortOrder: a.sortOrder,
        isPrimary: a.isPrimary ?? null,
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
      })));
  setPaths(mapPaths);
  setLocations(locs);
    } catch (e) {
      console.error(e);
      addNotification('Failed to load data', 'error');
    }
  };

  const handleEdit = (userId: string, userName: string) => {
    const userPrefs = preferences
      .filter(p => p.userId === userId)
      .sort((a,b) => a.sortOrder - b.sortOrder)
      .map(p => ({ ...p }));
    setEditing({ userId, userName, preferences: userPrefs });
    setEditingChanges({ toCreate: [], toUpdate: [], toDelete: [] });
  };

  const handleDeleteAllUserPreferences = async (userId: string) => {
    try {
      const userPrefs = preferences.filter(p => p.userId === userId && p.mapPathUserId != null);
      await Promise.all(userPrefs.map(p => hallPassApiService.deleteMapPathUser(p.mapPathUserId!)));
      await load();
      addNotification('All path assignments removed', 'success');
    } catch (err) {
      console.error(err);
      addNotification('Failed to remove path assignments', 'error');
    }
  };

  // helper to apply rank ordering + track changes (works for existing & newly created)
  const applyRankOrdering = (prefs: UserPathPreference[]) => {
    setEditingChanges(prev => {
      const updated = { ...prev };
      // ensure arrays are cloned (avoid in-place mutation side-effects)
      updated.toUpdate = [...updated.toUpdate];
      updated.toCreate = [...updated.toCreate];

      prefs.forEach((pref, idx) => {
        const newRank = idx + 1;
        if (pref.sortOrder === newRank) return;
        pref.sortOrder = newRank;
        const isTemp = pref.mapPathUserId == null || pref.id.startsWith('temp-');
        if (isTemp) {
          const createEntry = updated.toCreate.find(c => c.hallPassTypeId === pref.pathId);
          if (createEntry) createEntry.rankOrder = newRank; else updated.toCreate.push({ hallPassTypeId: pref.pathId, rankOrder: newRank });
          return;
        }
        const existingUpdate = updated.toUpdate.find(u => u.id === pref.id);
        if (existingUpdate) existingUpdate.rankOrder = newRank; else updated.toUpdate.push({ id: pref.id, hallPassTypeId: pref.pathId, rankOrder: newRank });
      });
      return updated;
    });
  };

  // replace old movePreference with unified reorder logic
  const movePreference = (index: number, direction: 'up' | 'down') => {
    if (!editing) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= editing.preferences.length) return;

    const newPrefs = editing.preferences.map(p => ({ ...p }));
    [newPrefs[index], newPrefs[newIndex]] = [newPrefs[newIndex], newPrefs[index]];
    applyRankOrdering(newPrefs);
    setEditing({ ...editing, preferences: newPrefs });
  };

  // drag & drop handlers
  const handleDragStart = (idx: number) => setDragIndex(idx);
  const handleDragOver = (e: React.DragEvent, overIdx: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === overIdx || !editing) return;
    const newPrefs = editing.preferences.map(p => ({ ...p }));
    const [moved] = newPrefs.splice(dragIndex, 1);
    newPrefs.splice(overIdx, 0, moved);
    applyRankOrdering(newPrefs);
    setEditing({ ...editing, preferences: newPrefs });
    setDragIndex(overIdx);
  };
  const handleDragEnd = () => setDragIndex(null);

  const handleSave = async () => {
  if (!editing?.userId) return;
    
    try {
      // Process deletions
      await Promise.all(editingChanges.toDelete.map(id => {
        const num = Number(id);
        if (!isNaN(num)) return hallPassApiService.deleteMapPathUser(num);
      }));

      // Process creations
      await Promise.all(editingChanges.toCreate.map(item => hallPassApiService.createMapPathUser({
        userId: editing.userId,
        pathId: item.hallPassTypeId,
        sortOrder: item.rankOrder,
      })));

      // Process updates
      await Promise.all(editingChanges.toUpdate.map(update => {
        const num = Number(update.id);
        if (isNaN(num)) return;
        return hallPassApiService.updateMapPathUser(num, {
          userId: editing.userId,
          pathId: update.hallPassTypeId,
          sortOrder: update.rankOrder,
        } as any);
      }))

      await load();
      setEditing(null);
      setEditingChanges({ toCreate: [], toUpdate: [], toDelete: [] });
  addNotification('User path assignments updated successfully', 'success');
    } catch (err) {
      console.error(err);
      addNotification('Failed to save changes', 'error');
    }
  };

  const userPreferenceGroups = users.map(user => {
    const userPrefs = preferences.filter(p => p.userId === user.id).sort((a,b)=>a.sortOrder-b.sortOrder);
    const detailed = userPrefs.map(p => ({ pref: p, path: paths.find(pt => pt.pathId === p.pathId) }));
    return { user, preferences: detailed };
  }).filter(group => {
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    return group.user.name.toLowerCase().includes(q) || group.preferences.some(p => {
      const pl = getPathLabel(p.path);
      return pl.toLowerCase().includes(q);
    });
  });

  // Helper: derive friendly label from path + locations
  const getPathLabel = (path?: MapPath) => {
    if (!path) return '';
    const start = locations.find(l => l.locationId === path.startLocationId);
    const end = locations.find(l => l.locationId === path.endLocationId);
    const startName = start?.name || path.startLocationId;
    const endName = end?.name || path.endLocationId;
    return `${startName} → ${endName}`;
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center">
  <h1 className="text-2xl font-bold">User Path Assignments</h1>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search users or paths..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 pr-3 py-2 w-full border rounded-md"
        />
      </div>

      <div className="bg-white border rounded p-4">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-4 py-2">User</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Path Order</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {userPreferenceGroups.map(group => {
              const prefElements = group.preferences.length === 0
                ? (
                  <span className="text-gray-500 text-xs">No assignments</span>
                )
                : group.preferences.map((entry, index) => (
                  <div key={entry.pref.id} className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-600">#{index + 1}</span>
                    <span className="px-2 py-1 bg-gray-100 text-xs rounded truncate max-w-[220px]" title={getPathLabel(entry.path)}>
                      {getPathLabel(entry.path)}
                    </span>
                    {index === 0 && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                        Primary
                      </span>
                    )}
                  </div>
                ));

              return (
                <tr key={group.user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{group.user.name}</td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                      {group.user.type}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-col gap-1">
                      {prefElements}
                    </div>
                  </td>
                  <td className="px-4 py-2 flex gap-2">
                    <button
                      onClick={() => handleEdit(group.user.id, group.user.name)}
                      className="text-indigo-600 hover:text-indigo-800"
                      title="Edit user preferences"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteAllUserPreferences(group.user.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Remove all preferences for this user"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                Manage Path Assignments for: {editing.userName}
              </h2>
              <button
                onClick={() => {
                  setEditing(null);
                  setEditingChanges({ toCreate: [], toUpdate: [], toDelete: [] });
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 text-sm text-gray-600">
              <p>Drag paths to reorder them. The first path will be considered the primary (default) path.</p>
            </div>

            <div className="space-y-4 max-h-[50vh] overflow-y-auto">
              {/* Current preferences */}
              {editing.preferences
                .filter(p => !editingChanges.toDelete.includes(p.id))
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((pref, index) => {
                  const path = paths.find(pt => pt.pathId === pref.pathId);
                  const isMarkedForDeletion = editingChanges.toDelete.includes(pref.id);
                  
                  return (
                    <div
                      key={pref.id}
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        isMarkedForDeletion ? 'opacity-50 bg-red-50' : ''
                      } ${dragIndex === index ? 'ring-2 ring-indigo-300' : 'cursor-move'}`}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => movePreference(index, 'up')}
                            disabled={index === 0}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => movePreference(index, 'down')}
                            disabled={index === editing.preferences.length - 1}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-600">#{index + 1}</span>
                            <div className="font-medium select-none">{getPathLabel(path)}</div>
                            {index === 0 && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                Default
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          setEditingChanges(prev => ({
                            ...prev,
                            toDelete: [...prev.toDelete, pref.id],
                            toUpdate: prev.toUpdate.filter(u => u.id !== pref.id),
                            toCreate: prev.toCreate.filter(c => c.hallPassTypeId !== pref.pathId)
                          }));
                        }}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm"
                        onMouseDown={e => e.stopPropagation()}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}

              {/* Add new preferences */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium mb-2">Available Paths</h3>
    {paths
                  .filter(path => 
                    !editing.preferences.some(p => p.pathId === path.pathId && !editingChanges.toDelete.includes(p.id)) &&
                    !editingChanges.toCreate.some(c => c.hallPassTypeId === path.pathId)
                  )
                  .map(path => (
                    <div key={path.pathId} className="flex items-center justify-between p-3 border rounded-lg mb-2">
                      <div>
                        <div className="font-medium">{getPathLabel(path)}</div>
                      </div>
                      
                      <button
                        onClick={() => {
                          const nextRankOrder = editing.preferences.length + editingChanges.toCreate.length + 1;
                          setEditingChanges(prev => ({
                            ...prev,
                            toCreate: [...prev.toCreate, { hallPassTypeId: path.pathId, rankOrder: nextRankOrder }]
                          }));
                          setEditing(prev => ({
                            ...prev!,
                            preferences: [...prev!.preferences, {
                              id: `temp-${Date.now()}`,
                              userId: prev!.userId,
                              pathId: path.pathId,
                              sortOrder: nextRankOrder
                            }]
                          }));
                        }}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 text-sm"
                      >
                        Add
                      </button>
                    </div>
                  ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditing(null);
                  setEditingChanges({ toCreate: [], toUpdate: [], toDelete: [] });
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600"
                disabled={
                  editingChanges.toCreate.length === 0 && 
                  editingChanges.toUpdate.length === 0 && 
                  editingChanges.toDelete.length === 0
                }
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserHallPassTypesPage;